export interface CsvColumn {
    index: number;
    name: string;
    samples: string[];
}

/** Parse up to maxRows from a CSV string (handles quoted fields and commas). */
function parseCsvPreview(text: string, maxRows: number): string[][] {
    const records: string[][] = [];
    let row: string[] = [];
    let field = "";
    let inQuotes = false;
    let i = 0;

    const pushField = () => {
        row.push(field);
        field = "";
    };

    const pushRow = () => {
        const isEmpty = row.length === 0 || (row.length === 1 && row[0] === "");
        if (!isEmpty) {
            records.push(row);
        }
        row = [];
    };

    while (i < text.length && records.length < maxRows) {
        const ch = text[i];

        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i += 2;
                    continue;
                }
                inQuotes = false;
            } else {
                field += ch;
            }
            i++;
            continue;
        }

        if (ch === '"') {
            inQuotes = true;
            i++;
        } else if (ch === ",") {
            pushField();
            i++;
        } else if (ch === "\r") {
            if (text[i + 1] === "\n") i++;
            pushField();
            pushRow();
            i++;
        } else if (ch === "\n") {
            pushField();
            pushRow();
            i++;
        } else {
            field += ch;
            i++;
        }
    }

    if (records.length < maxRows && (field !== "" || row.length > 0)) {
        pushField();
        pushRow();
    }

    return records;
}

// Parse a CSV string client-side and return column metadata with sample values.
// Only the first few rows are parsed (enough to label columns and show previews).
export function detectCsvColumns(
    text: string,
    hasHeader: boolean,
    sampleSize = 4
): CsvColumn[] {
    const maxRows = sampleSize + (hasHeader ? 2 : 1);
    const records = parseCsvPreview(text, maxRows);

    if (!records.length) return [];

    const headerRow = hasHeader ? records[0] : null;
    const dataRows = hasHeader ? records.slice(1) : records;
    const numCols = Math.max(...records.map((r) => r.length));
    const columns: CsvColumn[] = [];

    for (let i = 0; i < numCols; i++) {
        const samples = dataRows
            .map((r) => r[i])
            .filter((v) => v !== undefined && v !== "")
            .slice(0, sampleSize);

        columns.push({
            index: i,
            name: headerRow && headerRow[i] ? headerRow[i] : `Column ${i}`,
            samples,
        });
    }

    return columns;
}
